import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useAuthStore } from "@/stores/authStore";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import * as Sentry from "@sentry/react";

// On a new deploy, old hashed chunks no longer exist. Reload once so the
// browser fetches the new index.html (and its updated chunk references).
// The sessionStorage flag prevents an infinite reload loop if the chunk
// keeps failing after the reload (transient network error, CSP issue, etc.).
function lazyWithReload(factory: () => Promise<{ default: React.ComponentType }>) {
  return lazy(() =>
    factory().catch((err) => {
      const isChunkError =
        err?.name === "ChunkLoadError" ||
        /Loading chunk|Failed to fetch dynamically imported module/i.test(err?.message ?? "");

      if (isChunkError && !sessionStorage.getItem("chunk_reload_attempted")) {
        sessionStorage.setItem("chunk_reload_attempted", "1");
        globalThis.location.reload();
        return new Promise(() => {});
      }

      Sentry.captureException(err, { tags: { source: "lazyWithReload" } });
      throw err;
    })
  );
}

const Login = lazyWithReload(() => import("./pages/authentication/Login"));
const Register = lazyWithReload(() => import("./pages/authentication/Register"));
const ForgotPassword = lazyWithReload(() => import("./pages/authentication/ForgotPassword"));
const ResetPassword = lazyWithReload(() => import("./pages/authentication/ResetPassword"));
const VerifyEmail = lazyWithReload(() => import("./pages/authentication/VerifyEmail"));
const SetupAccount = lazyWithReload(() => import("./pages/authentication/SetupAccount"));
const JoinGroupPage = lazyWithReload(() => import("./pages/JoinGroupPage"));
const JoinViaLinkPage = lazyWithReload(() => import("./pages/JoinViaLinkPage"));
const Dashboard = lazyWithReload(() => import("./pages/Dashboard"));
const Members = lazyWithReload(() => import("./pages/Members"));
const MemberProfile = lazyWithReload(() => import("./pages/MemberProfile"));
const NotFound = lazyWithReload(() => import("./pages/NotFound"));
const Settings = lazyWithReload(() => import("./pages/Settings"));
const Loans = lazyWithReload(() => import("./pages/Loans"));
const LoanDetails = lazyWithReload(() => import("./pages/LoanDetails"));
const Investments = lazyWithReload(() => import("./pages/Investments"));
const InvestmentDetail = lazyWithReload(() => import("./pages/InvestmentDetail"));
const Reports = lazyWithReload(() => import("./pages/Reports"));
const Wallet = lazyWithReload(() => import("./pages/Wallet"));
const BulkTransactions = lazyWithReload(() => import("./pages/BulkTransactions"));
const AuditLog = lazyWithReload(() => import("./pages/AuditLog"));
const ProfileSettings = lazyWithReload(() => import("./pages/ProfileSettings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,   // 5 min — avoid refetches on quick navigation
      gcTime: 1000 * 60 * 30,     // 30 min — keep inactive data longer
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

function RootRedirect() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Root redirect */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/password-reset/:token" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/setup-account" element={<SetupAccount />} />
            <Route path="/groups/:uuid/join" element={<JoinGroupPage />} />
            <Route path="/join/:token" element={<JoinViaLinkPage />} />

            {/* Protected dashboard routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/loans" element={<Loans />} />
                <Route path="/loans/:id" element={<LoanDetails />} />
                <Route path="/investments" element={<Investments />} />
                <Route path="/investments/:uuid" element={<InvestmentDetail />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/wallet" element={<Wallet />} />
                <Route path="/bulk-transactions" element={<BulkTransactions />} />
                <Route path="/audit-log" element={<AuditLog />} />
                <Route path="/members" element={<Members />} />
                <Route path="/members/:uuid" element={<MemberProfile />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/profile" element={<ProfileSettings />} />
              </Route>
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
